// Admin -> Audit log -> "Ask AI". Directors/administrators ask plain-English questions
// about system usage ("who used the system most today?", "which client was busiest this
// week?"). The backend (/audit-log/ask) computes every number from the audit log and the
// AI only words the answer — so answers are grounded, never guessed.
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, Send, Loader2, RotateCcw, Info } from "lucide-react";

const PERIODS = [
    { id: "today", label: "Today" },
    { id: "yesterday", label: "Yesterday" },
    { id: "7d", label: "Last 7 days" },
    { id: "30d", label: "Last 30 days" },
];

const SUGGESTIONS = [
    "Who used the system the most?",
    "Which client was the busiest?",
    "Which project had the most activity?",
    "What were the directors and administrators doing?",
    "Which client portal users were active, and what did they do?",
    "Who used AI features, and for what?",
];

// Minimal, safe renderer for the AI's short answers: paragraphs, "- " bullets and **bold**.
// Builds React elements (no innerHTML), so model output can never inject markup.
function renderInline(text, keyBase) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") && part.endsWith("**")
            ? <strong key={`${keyBase}-${i}`} className="font-semibold text-[#22303F]">{part.slice(2, -2)}</strong>
            : <span key={`${keyBase}-${i}`}>{part}</span>);
}

function Answer({ text }) {
    const blocks = [];
    let bullets = [];
    const flush = () => {
        if (bullets.length) {
            blocks.push(<ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1">{bullets}</ul>);
            bullets = [];
        }
    };
    text.split("\n").forEach((raw, i) => {
        const line = raw.trim();
        if (!line) { flush(); return; }
        const m = /^([-*•]|\d+\.)\s+(.*)$/.exec(line);
        if (m) bullets.push(<li key={`li-${i}`}>{renderInline(m[2], `li-${i}`)}</li>);
        else { flush(); blocks.push(<p key={`p-${i}`}>{renderInline(line.replace(/^#+\s*/, ""), `p-${i}`)}</p>); }
    });
    flush();
    return <div className="space-y-2 text-[13px] leading-relaxed text-[#2D2D2D]">{blocks}</div>;
}

export default function AuditAskPanel() {
    const [period, setPeriod] = useState("today");
    const [input, setInput] = useState("");
    const [turns, setTurns] = useState([]); // {role, content, meta?}
    const [busy, setBusy] = useState(false);
    const endRef = useRef(null);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [turns, busy]);

    async function ask(question) {
        const q = (question ?? input).trim();
        if (!q || busy) return;
        setInput("");
        const history = turns.map(({ role, content }) => ({ role, content }));
        setTurns((t) => [...t, { role: "user", content: q }]);
        setBusy(true);
        try {
            const { data } = await api.post("/audit-log/ask", { question: q, period, history }, { timeout: 90000 });
            if (data.period && data.period !== period) setPeriod(data.period); // question named its own timeframe
            setTurns((t) => [...t, { role: "assistant", content: data.answer, meta: data }]);
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || "Something went wrong — please try again.";
            setTurns((t) => [...t, { role: "assistant", content: msg, error: true }]);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid="audit-ask-panel" className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12px] font-semibold mr-1" style={{ color: "#6F6A64" }}>Period</span>
                {PERIODS.map((p) => (
                    <button key={p.id} data-testid={`audit-ask-period-${p.id}`} onClick={() => setPeriod(p.id)}
                            className="px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors"
                            style={period === p.id ? { background: "#22303F", color: "#fff", borderColor: "#22303F" }
                                : { background: "#fff", color: "#6F6A64", borderColor: "#E5E0DA" }}>
                        {p.label}
                    </button>
                ))}
                {turns.length > 0 && (
                    <button data-testid="audit-ask-reset" onClick={() => setTurns([])} disabled={busy}
                            className="ml-auto h-8 px-2.5 rounded-[8px] border text-[12px] font-semibold inline-flex items-center gap-1.5 bg-white"
                            style={{ borderColor: "#E5E0DA", color: "#3e4b63" }}>
                        <RotateCcw className="w-3.5 h-3.5" /> New conversation
                    </button>
                )}
            </div>

            <div className="bg-white border rounded-[11px] shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 space-y-4 min-h-[220px] max-h-[560px] overflow-y-auto">
                    {turns.length === 0 && !busy && (
                        <div data-testid="audit-ask-empty" className="space-y-3">
                            <div className="flex items-center gap-2 text-[13px] font-semibold text-[#22303F]">
                                <Sparkles className="w-4 h-4" style={{ color: "#7A3EC2" }} /> Ask about how the system is being used
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {SUGGESTIONS.map((s) => (
                                    <button key={s} onClick={() => ask(s)}
                                            className="text-left px-3 py-2 rounded-[8px] border text-[12.5px] hover:bg-[#FCFBF9]"
                                            style={{ borderColor: "#E5E0DA", color: "#3e4b63" }}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {turns.map((t, i) => t.role === "user" ? (
                        <div key={i} className="flex justify-end">
                            <div className="max-w-[80%] px-3.5 py-2 rounded-[10px] text-[13px] text-white" style={{ background: "#22303F" }}>
                                {t.content}
                            </div>
                        </div>
                    ) : (
                        <div key={i} data-testid="audit-ask-answer" className="flex gap-3">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: "#7A3EC21A" }}>
                                <Sparkles className="w-3.5 h-3.5" style={{ color: "#7A3EC2" }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                {t.error ? <div className="text-[13px]" style={{ color: "#C23B3B" }}>{t.content}</div> : <Answer text={t.content} />}
                                {t.meta && (
                                    <div className="mt-2 text-[11px] flex items-center gap-1.5" style={{ color: "#9A968F" }}>
                                        <Info className="w-3 h-3" />
                                        Based on {t.meta.total_events} recorded events by {t.meta.active_users} users · {t.meta.period_label}
                                        {t.meta.pct_with_project < 50 && ` · only ${t.meta.pct_with_project}% linked to a project`}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex items-center gap-2 text-[12.5px]" style={{ color: "#9A968F" }}>
                            <Loader2 className="w-4 h-4 animate-spin" /> Reading the audit log…
                        </div>
                    )}
                    <div ref={endRef} />
                </div>
                <form onSubmit={(e) => { e.preventDefault(); ask(); }}
                      className="flex items-center gap-2 border-t px-3 py-2.5" style={{ borderColor: "#F0EDE8" }}>
                    <input data-testid="audit-ask-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
                           placeholder="e.g. Which client admins logged in this week and what did they do?"
                           className="flex-1 bg-transparent outline-none text-[13px] px-2" />
                    <button data-testid="audit-ask-send" type="submit" disabled={busy || !input.trim()}
                            className="h-8 px-3 rounded-[8px] text-[12px] font-semibold inline-flex items-center gap-1.5 text-white disabled:opacity-40"
                            style={{ background: "#22303F" }}>
                        <Send className="w-3.5 h-3.5" /> Ask
                    </button>
                </form>
            </div>
            <p className="text-[11px]" style={{ color: "#9A968F" }}>
                Counts recorded actions (logins, creates, updates, submissions, approvals, AI use), not page views, so people who mostly read reports will look quieter than they are.
            </p>
        </div>
    );
}
